import { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import { postServicesData, getServicesData } from '../../../api/apiServices';

export default function ServicesEditor() {
  const [servicesData, setServicesData] = useState({
    sectionTitle: '',
    sectionSubtitle: '',
    services: [],
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // 🧠 Prefill data from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getServicesData();
        if (response) {
          setServicesData(response);
        }
      } catch (error) {
        console.error('Error fetching services data:', error);
        setMessage('No saved data found. Please create new services.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // 🔧 Update handlers
  const handleSectionChange = (field, value) => {
    setServicesData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServiceChange = (index, field, value) => {
    setServicesData(prev => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === index ? { ...service, [field]: value } : service
      ),
    }));
  };

  const handleFeatureChange = (serviceIndex, featureIndex, value) => {
    setServicesData(prev => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === serviceIndex
          ? {
              ...service,
              features: service.features.map((feature, j) =>
                j === featureIndex ? value : feature
              ),
            }
          : service
      ),
    }));
  };

  // ➕ Add / Remove functions
  const addService = () => {
    setServicesData(prev => ({
      ...prev,
      services: [
        ...prev.services,
        {
          icon: 'ri-star-line',
          title: 'New Service',
          description: 'Service description',
          features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4'],
          price: 'Starting at $1,000',
        },
      ],
    }));
  };

  const removeService = index => {
    setServicesData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index),
    }));
  };

  const addFeature = serviceIndex => {
    setServicesData(prev => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === serviceIndex
          ? { ...service, features: [...service.features, 'New Feature'] }
          : service
      ),
    }));
  };

  const removeFeature = (serviceIndex, featureIndex) => {
    setServicesData(prev => ({
      ...prev,
      services: prev.services.map((service, i) =>
        i === serviceIndex
          ? { ...service, features: service.features.filter((_, j) => j !== featureIndex) }
          : service
      ),
    }));
  };

  // 💾 Save to backend
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await postServicesData(servicesData);
      setMessage('Services section saved successfully!');
    } catch (error) {
      console.error('Error saving services section:', error);
      setMessage('Failed to save services section.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-slate-500">Loading services data...</p>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800 flex items-center">
          <i className="ri-service-line mr-3 text-emerald-600"></i>
          Services Section Settings
        </h2>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg p-4 flex items-center justify-between z-50">
  <p className="text-slate-600 font-medium">
    {message || "All changes are saved locally until you click Save."}
  </p>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  {loading ? "Saving..." : "Save Services Section"}
                </Button>
              </div>
      </div>

      {/* Section Header Inputs */}
      <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Section Header</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 font-semibold mb-3">Section Title</label>
            <input
              type="text"
              value={servicesData.sectionTitle || ''}
              onChange={e => handleSectionChange('sectionTitle', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
          <div>
            <label className="block text-slate-700 font-semibold mb-3">Section Subtitle</label>
            <input
              type="text"
              value={servicesData.sectionSubtitle || ''}
              onChange={e => handleSectionChange('sectionSubtitle', e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
            />
          </div>
        </div>
      </div>

      {/* Services List */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800">Services</h3>
          <Button
            onClick={addService}
            size="sm"
            variant="outline"
            className="border-emerald-200 text-emerald-600 hover:bg-emerald-50"
          >
            <i className="ri-add-line mr-2"></i>
            Add Service
          </Button>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {servicesData.services.map((service, index) => (
            <div key={index} className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-800">Service {index + 1}</h4>
                {servicesData.services.length > 1 && (
                  <button
                    onClick={() => removeService(index)}
                    className="text-red-500 hover:text-red-700 transition-colors duration-300"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">Icon</label>
                    <input
                      type="text"
                      value={service.icon || ''}
                      onChange={e => handleServiceChange(index, 'icon', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-2">Price</label>
                    <input
                      type="text"
                      value={service.price || ''}
                      onChange={e => handleServiceChange(index, 'price', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-600 font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={service.title || ''}
                    onChange={e => handleServiceChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-slate-600 font-medium mb-2">Description</label>
                  <textarea
                    value={service.description || ''}
                    onChange={e => handleServiceChange(index, 'description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-slate-600 font-medium">Features</label>
                    <button
                      onClick={() => addFeature(index)}
                      className="text-emerald-600 hover:text-emerald-700 transition-colors duration-300"
                    >
                      <i className="ri-add-line text-lg"></i>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {service.features?.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={e =>
                            handleFeatureChange(index, featureIndex, e.target.value)
                          }
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                        />
                        {service.features.length > 1 && (
                          <button
                            onClick={() => removeFeature(index, featureIndex)}
                            className="text-red-500 hover:text-red-700 transition-colors duration-300"
                          >
                            <i className="ri-close-line text-lg"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
